// Case data tab — editable structured fact sheet.
//
// The user has two ways to fill the case file: type into the chat (AI
// records it through tools) or fill this form directly. Either path lands
// the value in case_facts.manual_facts on the server, which always wins
// over LLM-extracted values.
//
// Layout rules (Refactoring UI):
//   - No bordered section cards. Sections are pure spacing + a small
//     uppercase eyebrow heading. Boxes around obvious things are noise.
//   - Field widths follow content. Phone, NIF, dates, amounts are short;
//     names are medium; emails and addresses are wide; multiline grievance
//     and work-scope span the full row. The previous all-fields-equal-width
//     grid wasted horizontal space and made hierarchy unreadable.
//   - Source indicator is a quiet right-aligned text on the label row, NOT
//     a pill. Empty fields render nothing — the empty input speaks for
//     itself. AI-detected fields show a faint "AI" hint; user-edited
//     fields show "edited · undo" so the user can hand the field back.
//   - Currency fields get a € prefix inside the input. Date inputs use
//     native pickers.

import { useEffect, useRef, useState } from 'react';
import {
  type CaseFacts,
  type CaseFactsResponse,
  useCaseFacts,
  useClearCaseFact,
  useUpdateCaseFact,
} from '../case-facts';
import { m } from '../paraglide/messages';

type FieldSize = 'sm' | 'md' | 'lg' | 'full';
type FieldInputType = 'string' | 'currency' | 'date' | 'multiline';

type FieldDef = {
  path: string;
  label: () => string;
  inputType: FieldInputType;
  size: FieldSize;
};

const SECTION_HOMEOWNER: FieldDef[] = [
  { path: 'homeowner.name', label: () => 'Name', inputType: 'string', size: 'md' },
  { path: 'homeowner.email', label: () => 'Email', inputType: 'string', size: 'lg' },
  { path: 'homeowner.phone', label: () => 'Phone', inputType: 'string', size: 'sm' },
  { path: 'homeowner.nif', label: () => 'NIF', inputType: 'string', size: 'sm' },
  { path: 'homeowner.address', label: () => 'Address', inputType: 'string', size: 'lg' },
];

const SECTION_CONTRACTOR: FieldDef[] = [
  { path: 'contractor.name', label: () => 'Name', inputType: 'string', size: 'md' },
  { path: 'contractor.company', label: () => 'Company', inputType: 'string', size: 'md' },
  { path: 'contractor.email', label: () => 'Email', inputType: 'string', size: 'lg' },
  { path: 'contractor.phone', label: () => 'Phone', inputType: 'string', size: 'sm' },
  { path: 'contractor.nif', label: () => 'NIF', inputType: 'string', size: 'sm' },
  { path: 'contractor.address', label: () => 'Address', inputType: 'string', size: 'lg' },
  { path: 'contractor.impicAlvara', label: () => 'IMPIC alvará', inputType: 'string', size: 'md' },
];

const SECTION_CONTRACT: FieldDef[] = [
  { path: 'contract.agreedAt', label: () => 'Agreed on', inputType: 'date', size: 'sm' },
  {
    path: 'contract.totalAmountEur',
    label: () => 'Total amount',
    inputType: 'currency',
    size: 'sm',
  },
  {
    path: 'contract.advancePaidEur',
    label: () => 'Advance paid',
    inputType: 'currency',
    size: 'sm',
  },
  { path: 'contract.finalPaidEur', label: () => 'Final paid', inputType: 'currency', size: 'sm' },
  {
    path: 'contract.completionDeadline',
    label: () => 'Completion deadline',
    inputType: 'date',
    size: 'sm',
  },
  {
    path: 'contract.propertyAddress',
    label: () => 'Property address',
    inputType: 'string',
    size: 'lg',
  },
  { path: 'contract.workScope', label: () => 'Work scope', inputType: 'multiline', size: 'full' },
];

const SECTION_DISPUTE: FieldDef[] = [
  {
    path: 'dispute.primaryGrievance',
    label: () => 'What went wrong',
    inputType: 'multiline',
    size: 'full',
  },
  {
    path: 'dispute.abandonmentDate',
    label: () => 'Abandonment date',
    inputType: 'date',
    size: 'sm',
  },
];

export function CaseDataPane({ caseId }: { caseId: string }) {
  const facts = useCaseFacts(caseId);

  // Listen for `claimpack:reveal-target` with kind='fact' — dispatched
  // when the user clicks "Add Contractor NIF →" on the artifacts pack
  // card. We scroll to the field, focus its input, and flash a soft
  // highlight so the click visibly lands somewhere instead of silently
  // switching the tab. Without this the click felt broken.
  useEffect(() => {
    function onReveal(e: Event) {
      const detail = (e as CustomEvent<{ kind?: string; path?: string }>).detail;
      if (!detail || detail.kind !== 'fact' || !detail.path) return;
      const inputId = `f-${detail.path.replace(/\./g, '-')}`;
      // Wait one frame more for the section to be in the DOM after the
      // tab switch.
      requestAnimationFrame(() => {
        const el = document.getElementById(inputId) as HTMLElement | null;
        if (!el) return;
        const field = el.closest('.field') as HTMLElement | null;
        (field ?? el).scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus({ preventScroll: true });
        if (field) {
          field.classList.add('is-flashed');
          window.setTimeout(() => field.classList.remove('is-flashed'), 2400);
        }
      });
    }
    window.addEventListener('claimpack:reveal-target', onReveal);
    return () => window.removeEventListener('claimpack:reveal-target', onReveal);
  }, []);

  if (facts.isLoading) return <p className="muted small">Loading…</p>;
  const data = facts.data;
  if (!data) return null;
  const manualPaths = new Set(data.manualPaths ?? []);

  return (
    <div className="case-data-pane">
      <p className="case-data-intro small muted">{m.caseDataIntro()}</p>

      <Section title={m.caseDataSectionHomeowner()}>
        {SECTION_HOMEOWNER.map((f) => (
          <FactField
            key={f.path}
            caseId={caseId}
            field={f}
            facts={data.facts}
            manualPaths={manualPaths}
          />
        ))}
      </Section>

      <Section title={m.caseDataSectionContractor()}>
        {SECTION_CONTRACTOR.map((f) => (
          <FactField
            key={f.path}
            caseId={caseId}
            field={f}
            facts={data.facts}
            manualPaths={manualPaths}
          />
        ))}
      </Section>

      <Section title={m.caseDataSectionContract()}>
        {SECTION_CONTRACT.map((f) => (
          <FactField
            key={f.path}
            caseId={caseId}
            field={f}
            facts={data.facts}
            manualPaths={manualPaths}
          />
        ))}
      </Section>

      <Section title={m.caseDataSectionDispute()}>
        {SECTION_DISPUTE.map((f) => (
          <FactField
            key={f.path}
            caseId={caseId}
            field={f}
            facts={data.facts}
            manualPaths={manualPaths}
          />
        ))}
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="case-data-section">
      <h3 className="case-data-section-head">{title}</h3>
      <div className="case-data-fields">{children}</div>
    </section>
  );
}

function FactField({
  caseId,
  field,
  facts,
  manualPaths,
}: {
  caseId: string;
  field: FieldDef;
  facts: CaseFacts | null;
  manualPaths: Set<string>;
}) {
  const update = useUpdateCaseFact(caseId);
  const clear = useClearCaseFact(caseId);
  const initial = readByPath(facts, field.path);
  const [draft, setDraft] = useState<string>(initial == null ? '' : String(initial));
  const lastSavedRef = useRef<string>(initial == null ? '' : String(initial));

  // External updates (from AI / refresh) reflect in the input when the
  // user isn't actively editing.
  useEffect(() => {
    const next = initial == null ? '' : String(initial);
    if (next !== lastSavedRef.current) {
      lastSavedRef.current = next;
      setDraft(next);
    }
  }, [initial]);

  function commit() {
    if (draft === lastSavedRef.current) return;
    const value: string | number | null =
      draft.trim() === ''
        ? null
        : field.inputType === 'currency'
          ? draft // server coerces string→number with locale tolerance
          : draft.trim();
    update.mutate({ path: field.path, value });
    lastSavedRef.current = draft;
  }

  function reset() {
    clear.mutate(field.path);
    setDraft('');
    lastSavedRef.current = '';
  }

  const isManual = manualPaths.has(field.path);
  const filled = initial != null && String(initial).trim() !== '';
  const inputId = `f-${field.path.replace(/\./g, '-')}`;

  // What goes on the right of the label row.
  // - Empty: nothing. The blank input is enough signal.
  // - AI-detected: faint "AI" hint so the user knows the value isn't theirs.
  // - User-edited: olive "edited · undo" so they can revert.
  let sourceNode: React.ReactNode = null;
  if (isManual) {
    sourceNode = (
      <span className="field-source field-source--manual">
        {m.caseDataSourceYou()}
        <button
          type="button"
          className="field-reset"
          onClick={reset}
          disabled={clear.isPending}
          title={m.caseDataResetToAi()}
        >
          {m.caseDataResetShort()}
        </button>
      </span>
    );
  } else if (filled) {
    sourceNode = <span className="field-source">{m.caseDataSourceAi()}</span>;
  }

  return (
    <div className={`field field-${field.size}`}>
      <div className="field-head">
        <label htmlFor={inputId} className="field-label">
          {field.label()}
        </label>
        {sourceNode}
      </div>
      {field.inputType === 'multiline' ? (
        <textarea
          id={inputId}
          className="field-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          rows={3}
        />
      ) : field.inputType === 'currency' ? (
        <div className="field-input-wrap">
          <span className="field-prefix" aria-hidden="true">
            €
          </span>
          <input
            id={inputId}
            className="field-input field-input--has-prefix"
            type="text"
            inputMode="decimal"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
          />
        </div>
      ) : (
        <input
          id={inputId}
          className="field-input"
          type={field.inputType === 'date' ? 'date' : 'text'}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
        />
      )}
    </div>
  );
}

function readByPath(facts: CaseFactsResponse['facts'], path: string): unknown {
  if (!facts) return null;
  const parts = path.split('.');
  // biome-ignore lint/suspicious/noExplicitAny: traversing dynamic shape
  let cursor: any = facts;
  for (const p of parts) {
    if (cursor == null || typeof cursor !== 'object') return null;
    cursor = cursor[p];
  }
  return cursor ?? null;
}
